import React, { useState } from 'react';
import { CANCEL_REASONS, SHOW_CAREGIVER_SESSION_HINTS } from '../config/appConfig';
import { stripAssigneeTag } from '../api/assignments';
import CoverageRequestSheet from '../components/CoverageRequestSheet';
import { CoverageIcon, ReminderIcon, RecurringIcon } from '../components/ui/icons';

// ─── EVENT CARD ───────────────────────────────────────────────────────────────
// Compact assign strip: care partners (+ any covering sitter session) stay
// top-level; family caregivers, the standard-sitter picker, reminder and
// cancel live behind a (⋯) menu. Recurring events get an inline "this day or
// the whole series?" prompt before any assign/reminder/ignore actually commits.
//
// Three distinct "doesn't need an assignee" states, not one:
//  - Covered (event.isSitterBlock)  — a sitter session IS the coverage. Full
//    visibility, no fade, no bell — this is resolved, not informational.
//  - Reminder (fohMeta.reminder)    — informational only, nothing to assign,
//    but must stay visible so it isn't forgotten. Bell icon, full opacity.
//  - Ignored                        — hidden entirely via a local-only
//    Firestore record; never written back to the source calendar. Available
//    on any event (not just detected-read-only ones — that detection is a
//    heuristic and can be wrong), reachable from the (⋯) menu, or as the
//    lone top-level action on events we already know are read-only.

function timeStrToMins(t) {
  if (!t) return NaN;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}
function dtToMins(dt) {
  if (!dt) return NaN;
  const d = new Date(dt);
  return d.getHours() * 60 + d.getMinutes();
}

export default function EventCard({ event, pending, onAssign, onToggleReminder, onCancel, onIgnore, carePartners, familySitters = [], sitterSessions = [], allSitters = [] }) {
  // openMenu: null | 'more' | 'cancel' — mutually exclusive pop-downs
  const [openMenu, setOpenMenu] = useState(null);
  const [scopeChoice, setScopeChoice] = useState(null); // { type: 'assign', code } | { type: 'reminder' } | { type: 'ignore' }
  const [conflictAck, setConflictAck] = useState(null);
  const [coverageSheetOpen, setCoverageSheetOpen] = useState(false);

  const isCancelled     = pending?.cancelled;
  const isSitterCovered = event.isSitterBlock;                        // covered via a sitter session — resolved, not a reminder
  const isReminder      = pending?.reminder || event.fohMeta?.reminder; // informational only — stays fully visible
  const isDeclined      = event.isDeclinedSession;                     // the caregiver backed out — needs a NEW one, not a reminder to ignore
  const currentAssignee = pending?.assignee || event.fohMeta?.assignee;
  const isReadOnly      = event.readOnly;
  const isRecurring     = !!event._raw?.recurringEventId;

  // Determine which sitter sessions cover this event's time window
  const isAllDay = !event._raw?.start?.dateTime;
  const evtStart = isAllDay ? 0    : dtToMins(event._raw?.start?.dateTime);
  const evtEnd   = isAllDay ? 1440 : dtToMins(event._raw?.end?.dateTime);

  // Suppressed for now (SHOW_CAREGIVER_SESSION_HINTS) — a booked caregiver's
  // time window overlapping this event doesn't mean they own it; that can't
  // be assumed. Computing an empty list here keeps the quick-assign button
  // below from ever populating without touching its render logic.
  const coveringSessions = SHOW_CAREGIVER_SESSION_HINTS ? sitterSessions.filter(sess => {
    if (isAllDay) return true;
    const sStart = timeStrToMins(sess.startTime);
    const sEnd   = timeStrToMins(sess.endTime);
    return !isNaN(sStart) && !isNaN(sEnd) && sStart < evtEnd && sEnd > evtStart;
  }) : [];

  // Standard (non-family) active sitters — the sitter picker's audience.
  // Family caregivers (G/D) get their own quick-assign row, not this picker.
  const standardSitters = allSitters.filter(s => s.sitterType !== 'family' && s.active !== false);

  function assignNow(code, scope) {
    onAssign(code, scope);
    const cp = carePartners.find(c => c.identifier === code);
    if (cp?.hasConflict) {
      setConflictAck(`Noted — ${cp.name?.split(' ')[0]} has a conflict. Assignment saved.`);
      setTimeout(() => setConflictAck(null), 3000);
    }
  }

  // Entry point for every assign action — recurring events get a scope prompt first.
  function handleAssignAttempt(code) {
    setOpenMenu(null);
    if (isRecurring) { setScopeChoice({ type: 'assign', code }); return; }
    assignNow(code, 'instance');
  }

  function handleReminderAttempt() {
    setOpenMenu(null);
    if (isRecurring) { setScopeChoice({ type: 'reminder' }); return; }
    onToggleReminder('instance');
  }

  // Ignore is offered on every event — the read-only heuristic (organizer.self)
  // can misjudge a subscribed/shared calendar, so hiding noise you don't want
  // shouldn't depend on getting that detection right.
  function handleIgnoreAttempt() {
    if (isRecurring) { setScopeChoice({ type: 'ignore' }); return; }
    onIgnore('instance');
  }

  function commitScope(scope) {
    if (scopeChoice?.type === 'assign')        assignNow(scopeChoice.code, scope);
    else if (scopeChoice?.type === 'reminder') onToggleReminder(scope);
    else if (scopeChoice?.type === 'ignore')   onIgnore(scope);
    setScopeChoice(null);
  }

  return (
    <div className={`event-card ${isCancelled ? 'event-card--cancelled' : ''} ${isReminder ? 'event-card--reminder' : ''}`}>
      {/* Event title + time */}
      <div className="event-card__top">
        <span className="event-card__cal-dot" style={{ backgroundColor: event.calendarColor || '#aaa' }} />
        <div className="event-card__info">
          <span className="event-card__title">
            {stripAssigneeTag(event.summary)}
            {isRecurring && <span className="recurring-glyph" title="Part of a recurring series" aria-label="Recurring"><RecurringIcon /></span>}
          </span>
          <span className="event-card__time">{event.startTime || 'All day'}</span>
        </div>
        {isCancelled && <span className="badge badge--gray" style={{ fontSize: 10 }}>Cancelled</span>}
        {isDeclined && !isCancelled && (
          <span className="badge badge--red" style={{ fontSize: 10 }} title="The caregiver declined this session — needs a new one">
            DECLINED — needs a new caregiver
          </span>
        )}
        {isReadOnly  && <span className="badge badge--sage" style={{ fontSize: 10 }} title="Read-only calendar — Grove can't write back to it">view only</span>}
        {isReminder && (
          <span className="event-card__reminder-flag" title="Reminder — informational only, no assignment needed"><ReminderIcon /></span>
        )}
        {currentAssignee && !isCancelled && !(isReminder && isRecurring) && (
          <span className="event-card__assigned-check" title={`Assigned to (${currentAssignee})`}>✓</span>
        )}
      </div>

      {/* Read-only events (e.g. school calendar) get no write-capable controls —
          just an Ignore action, which hides locally without touching the calendar. */}
      {!isCancelled && isReadOnly && (
        <div className="event-card__actions">
          <button
            className="assignee-btn assignee-btn--cancel"
            onClick={handleIgnoreAttempt}
            title="Hide this in Grove — doesn't touch the calendar"
            aria-label="Ignore — hide this in Grove"
          >🙈</button>
        </div>
      )}

      {/* Compact assign strip — parents + covering sitter sessions top-level, everything else in (⋯) */}
      {!isCancelled && !isReadOnly && (
        <div className="event-card__actions">
          {!isSitterCovered && !(isReminder && isRecurring) && carePartners.map(cp => (
            <button
              key={cp.identifier}
              className={`assignee-btn ${currentAssignee === cp.identifier ? 'assignee-btn--active' : ''}`}
              onClick={() => handleAssignAttempt(cp.identifier)}
              title={cp.hasConflict ? `${cp.name} — has conflict, can still assign` : cp.name}
            >
              ({cp.identifier}){cp.hasConflict && <span className="conflict-indicator"> ⚠</span>}
            </button>
          ))}
          {/* Sitter session buttons — sitters with a scheduled block covering this event; rare & contextual, stays top-level */}
          {!isSitterCovered && !(isReminder && isRecurring) && coveringSessions.map((sess, i) => {
            const sitter = allSitters.find(s => s.id === sess.sitterId);
            const code   = sitter?.identifier || sitter?.firstName?.[0]?.toUpperCase() || sess.sitterName?.split(' ')?.[0]?.[0]?.toUpperCase() || 'S';
            const name   = sitter?.firstName || sess.sitterName?.split(' ')[0] || 'Caregiver';
            return (
              <button key={`sess-${i}`}
                className={`assignee-btn assignee-btn--sitter ${currentAssignee === code ? 'assignee-btn--active' : ''}`}
                onClick={() => handleAssignAttempt(code)}
                title={`${name} — caregiver session ${sess.startTime}–${sess.endTime}`}>
                ({code})🌱
              </button>
            );
          })}
          {/* Reminder toggle stays top-level, not tucked behind (⋯) — whether an
              event has a reminder set is worth seeing without opening a menu. */}
          {!isSitterCovered && !isReminder && (
            <button
              className="assignee-btn assignee-btn--reminder"
              onClick={handleReminderAttempt}
              title="Mark as a reminder — informational only, no assignee needed, stays visible"
            ><ReminderIcon /></button>
          )}
          {!isSitterCovered && !(isReminder && isRecurring) && (
            <button
              className={`assignee-btn assignee-btn--more ${openMenu === 'more' ? 'assignee-btn--active' : ''}`}
              onClick={() => setOpenMenu(openMenu === 'more' ? null : 'more')}
              title="More options — family, caregivers, cancel"
            >⋮</button>
          )}
          {!isSitterCovered && !isReminder && currentAssignee && (
            <button
              className="assignee-btn assignee-btn--coverage"
              onClick={() => setCoverageSheetOpen(true)}
              title="Ask someone to cover this"
              aria-label="Ask someone to cover this"
            ><CoverageIcon /></button>
          )}
          {/* Reminder stays reachable even when already set (to clear it) */}
          {isReminder && (
            <button
              className="assignee-btn assignee-btn--reminder-active"
              onClick={handleReminderAttempt}
              title="Clear reminder — this event will need an assignee again"
            ><ReminderIcon /></button>
          )}
          {/* Covered events skip the (⋯) menu entirely, so Cancel needs its
              own top-level entry point here — otherwise there's no way to
              cancel/delete a caregiver session from Review & Assign at all. */}
          {isSitterCovered && (
            <button
              className="assignee-btn assignee-btn--cancel"
              onClick={() => setOpenMenu('cancel')}
              title="Cancel this caregiver session — removes it from the calendar and clears its badges"
              aria-label="Cancel this caregiver session"
            >✕</button>
          )}
        </div>
      )}

      {/* (⋯) menu — Family quick-assign, Sitter picker, Reminder, Cancel */}
      {openMenu === 'more' && !isSitterCovered && !(isReminder && isRecurring) && !isReadOnly && (
        <div className="more-menu">
          {familySitters.length > 0 && (
            <div className="more-menu__row">
              <span className="more-menu__label">Family</span>
              {familySitters.map(fs => (
                <button
                  key={fs.identifier}
                  className={`assignee-btn assignee-btn--family ${currentAssignee === fs.identifier ? 'assignee-btn--active' : ''}`}
                  onClick={() => handleAssignAttempt(fs.identifier)}
                  title={`${fs.name}${fs.pickupEligible ? ' · pickup eligible' : ''}`}
                >({fs.identifier})</button>
              ))}
            </div>
          )}
          <div className="more-menu__row">
            <span className="more-menu__label">Caregiver{standardSitters.length !== 1 ? 's' : ''}</span>
            {standardSitters.length === 0 ? (
              <span className="more-menu__empty">No caregivers in family setup</span>
            ) : standardSitters.map(s => {
              const code = s.identifier || s.firstName?.[0]?.toUpperCase() || '?';
              return (
                <button key={s.id} className={`assignee-btn assignee-btn--sitter ${currentAssignee === code ? 'assignee-btn--active' : ''}`}
                  onClick={() => handleAssignAttempt(code)}>
                  {s.firstName}
                </button>
              );
            })}
          </div>
          <div className="more-menu__divider" />
          <div className="more-menu__row">
            <span className="more-menu__label">Actions</span>
            <button
              className="assignee-btn assignee-btn--cancel"
              onClick={handleIgnoreAttempt}
              title="Hide this in Grove — doesn't touch the calendar"
            >🙈</button>
            <button
              className="assignee-btn assignee-btn--cancel"
              onClick={() => setOpenMenu('cancel')}
              title="Cancel this event"
            >✕</button>
          </div>
        </div>
      )}

      {/* Recurring scope chooser — appears before any assign/reminder/ignore commits on a series */}
      {scopeChoice && (
        <div className="scope-chooser">
          <span className="scope-chooser__label"><span className="recurring-glyph recurring-glyph--lead" aria-hidden="true"><RecurringIcon /></span>Recurring — apply to:</span>
          <button className="assignee-btn" onClick={() => commitScope('instance')}>Just this day</button>
          <button className="assignee-btn" onClick={() => commitScope('series')}>All occurrences</button>
          <button className="assignee-btn assignee-btn--cancel" onClick={() => setScopeChoice(null)}>✕</button>
        </div>
      )}

      {conflictAck && <div className="conflict-ack">{conflictAck}</div>}

      {coverageSheetOpen && (
        <CoverageRequestSheet
          event={{ ...event, start: event._raw?.start, description: event._raw?.description }}
          myId={currentAssignee}
          mode="future"
          onClose={() => setCoverageSheetOpen(false)}
          onDone={() => setCoverageSheetOpen(false)}
        />
      )}

      {openMenu === 'cancel' && (
        <div className="cancel-reasons">
          <span className="cancel-reasons__label">Reason:</span>
          {CANCEL_REASONS.map(r => (
            <button key={r.id} className="cancel-reason-btn" onClick={() => {
              onCancel(r.id);
              setOpenMenu(null);
            }}>{r.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
